import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MyProducts from './MyProducts';
import FarmerHubConnections from './FarmerHubConnections';
import FarmerInbox from './FarmerInbox';
import VendorCreditReport from '../components/VendorCreditReport';
import EducationModuleManager from '../components/EducationModuleManager';
import Accordion from '../components/Accordion';
import { VendorFavoritesDashboard } from '../system-modules/favorites/components';
import BuyerInbox from '../system-modules/favorites/components/BuyerInbox';
import PaymentSetup from '../components/PaymentSetup';

export default function ProducerDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('products');
  const [creditSettings, setCreditSettings] = useState({ enabled: false, percentage: 5 });
  const [savingSettings, setSavingSettings] = useState(false);
  const [storefront, setStorefront] = useState({
    bannerImage: '',
    primaryColor: '#16a34a',
    secondaryColor: '#15803d',
    layoutStyle: 'grid',
    showTeam: true,
    customMessage: '',
    teamMembers: [],
    overlayText: true
  });
  const [savingStorefront, setSavingStorefront] = useState(false);
  const [educationModules, setEducationModules] = useState([]);
  const [loadingEducation, setLoadingEducation] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchCreditSettings();
    fetchStorefrontSettings();
    fetchEducationModules();
    fetchUnreadCount();
  }, []);

  const fetchCreditSettings = async () => {
    try {
      const res = await fetch('/api/credit-settings/settings', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCreditSettings(data);
      }
    } catch (err) {
      console.error('Failed to fetch credit settings:', err);
    }
  };

  const fetchStorefrontSettings = async () => {
    if (!user?._id) return;
    try {
      const res = await fetch(`/api/users/storefront/${user._id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.storefront) {
          setStorefront(prev => ({ ...prev, ...data.storefront }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch storefront settings:', err);
    }
  };

  const fetchEducationModules = async () => {
    setLoadingEducation(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/education-modules', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEducationModules(data);
      }
    } catch (err) {
      console.error('Failed to fetch education modules:', err);
    } finally {
      setLoadingEducation(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch('/api/favorites/communication/unread/count', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const saveCreditSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch('/api/credit-settings/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(creditSettings)
      });
      if (res.ok) {
        alert('Credit settings saved successfully');
      } else {
        const error = await res.json();
        alert(error.message || 'Failed to save settings');
      }
    } catch (err) {
      alert('Error saving settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const saveStorefront = async () => {
    setSavingStorefront(true);
    try {
      const payload = {
        bannerImage: storefront.bannerImage,
        primaryColor: storefront.primaryColor,
        secondaryColor: storefront.secondaryColor,
        layoutStyle: storefront.layoutStyle,
        showTeam: storefront.showTeam,
        customMessage: storefront.customMessage,
        teamMembers: storefront.teamMembers || [],
        overlayText: storefront.overlayText
      };
      
      const res = await fetch('/api/users/storefront', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        alert('Storefront settings saved');
      } else {
        const error = await res.json();
        alert(error.message || 'Failed to save');
      }
    } catch (err) {
      console.error('Save error:', err);
      alert('Error saving settings');
    } finally {
      setSavingStorefront(false);
    }
  };

  const accordionItems = [
    {
      icon: '📦',
      title: 'My Products',
      badge: 'Manage',
      content: <MyProducts />
    },
    {
      icon: '🔗',
      title: 'Hub Connections',
      badge: 'Connect',
      content: <FarmerHubConnections />
    },
    {
      icon: '📨',
      title: 'Hub Invitations',
      badge: 'Invites',
      content: <FarmerInbox />
    },
    {
      icon: '💰',
      title: 'Credit Settings',
      content: (
        <div className="space-y-4">
          <h3 className="font-semibold">Buyer Credit Program</h3>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={creditSettings.enabled}
              onChange={(e) => setCreditSettings({ ...creditSettings, enabled: e.target.checked })}
              className="w-4 h-4"
            />
            <span>Enable buyer credits for your farm</span>
          </label>
          {creditSettings.enabled && (
            <div>
              <label className="block text-sm font-medium mb-1">
                Credit Percentage ({creditSettings.percentage}%)
              </label>
              <input
                type="range"
                min="1"
                max="25"
                step="1"
                value={creditSettings.percentage}
                onChange={(e) => setCreditSettings({ ...creditSettings, percentage: parseInt(e.target.value) })}
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Buyers will earn {creditSettings.percentage}% back in credits on every purchase.
              </p>
            </div>
          )}
          <button
            onClick={saveCreditSettings}
            disabled={savingSettings}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            {savingSettings ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      )
    },
    {
      icon: '📊',
      title: 'Credit Reports',
      content: <VendorCreditReport type="producer" title="Credit Activity" />
    },
    {
      icon: '💳',
      title: 'Payment Setup',
      badge: 'Stripe',
      content: <PaymentSetup />
    },
    {
      icon: '❤️',
      title: 'Favorites',
      badge: 'Stats',
      content: <VendorFavoritesDashboard />
    },
    {
      icon: '📬',
      title: 'Inbox',
      badge: unreadCount > 0 ? `${unreadCount} new` : 'Messages',
      content: <BuyerInbox />
    },
    {
      icon: '🏪',
      title: 'Storefront',
      content: (
        <div className="space-y-4">
          <h3 className="font-semibold">Customize Your Storefront</h3>
          <div>
            <label className="block text-sm font-medium mb-1">Banner Image</label>
            <input 
              type="file" 
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('image', file);
                try {
                  const res = await fetch('/api/upload/storefront', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                    body: formData
                  });
                  const data = await res.json();
                  if (data.url) {
                    setStorefront({ ...storefront, bannerImage: data.url });
                    alert('Banner uploaded');
                  }
                } catch (err) {
                  alert('Upload failed');
                }
              }}
              className="w-full border rounded px-3 py-2"
            />
            {storefront.bannerImage && (
              <div className="mt-2">
                <img src={storefront.bannerImage} alt="Banner" className="h-32 object-cover rounded" />
                <button onClick={() => setStorefront({ ...storefront, bannerImage: '' })} className="text-red-500 text-sm mt-1">
                  Remove
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Primary Color</label>
            <input 
              type="color" 
              value={storefront.primaryColor}
              onChange={(e) => setStorefront({ ...storefront, primaryColor: e.target.value })}
              className="w-12 h-12 border rounded cursor-pointer"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Secondary Color</label>
            <input 
              type="color" 
              value={storefront.secondaryColor}
              onChange={(e) => setStorefront({ ...storefront, secondaryColor: e.target.value })}
              className="w-12 h-12 border rounded cursor-pointer"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Custom Message</label>
            <textarea 
              value={storefront.customMessage}
              onChange={(e) => setStorefront({ ...storefront, customMessage: e.target.value })}
              rows="3"
              className="w-full border rounded px-3 py-2"
              placeholder="Welcome to our farm!"
            />
          </div>
          <div className="flex items-center gap-2">
            <input 
              type="checkbox" 
              checked={storefront.showTeam}
              onChange={(e) => setStorefront({ ...storefront, showTeam: e.target.checked })}
              className="w-4 h-4"
            />
            <span>Show team section</span>
          </div>
          {storefront.showTeam && (
            <div>
              <h4 className="font-medium mb-2">Team Members</h4>
              {storefront.teamMembers?.map((member, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <input 
                    type="text" 
                    placeholder="Name" 
                    value={member.name}
                    onChange={(e) => {
                      const newTeam = [...(storefront.teamMembers || [])];
                      newTeam[idx].name = e.target.value;
                      setStorefront({ ...storefront, teamMembers: newTeam });
                    }}
                    className="flex-1 border rounded px-2 py-1"
                  />
                  <input 
                    type="text" 
                    placeholder="Role" 
                    value={member.role}
                    onChange={(e) => {
                      const newTeam = [...(storefront.teamMembers || [])];
                      newTeam[idx].role = e.target.value;
                      setStorefront({ ...storefront, teamMembers: newTeam });
                    }}
                    className="flex-1 border rounded px-2 py-1"
                  />
                  <button 
                    onClick={() => {
                      const newTeam = storefront.teamMembers.filter((_, i) => i !== idx);
                      setStorefront({ ...storefront, teamMembers: newTeam });
                    }} 
                    className="text-red-500 px-2"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button 
                onClick={() => setStorefront({ 
                  ...storefront, 
                  teamMembers: [...(storefront.teamMembers || []), { name: '', role: '' }] 
                })} 
                className="text-green-600 text-sm mt-2"
              >
                + Add Team Member
              </button>
            </div>
          )}
          <button
            onClick={saveStorefront}
            disabled={savingStorefront}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 w-full"
          >
            {savingStorefront ? 'Saving...' : 'Save Storefront Settings'}
          </button>
        </div>
      )
    },
    {
      icon: '📚',
      title: 'Education',
      badge: educationModules.length || 0,
      content: (
        <EducationModuleManager 
          role="producer"
          modules={educationModules}
          onRefresh={fetchEducationModules}
          loading={loadingEducation}
        />
      )
    }
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 py-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Producer Dashboard</h1>
        {user?.slug && (
          <Link to={`/producer/${user.slug}`} target="_blank" className="bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700">
            View Storefront
          </Link>
        )}
      </div>
      {user?.slug && (
        <div className="text-sm text-gray-600 mb-4">
          URL: <code className="bg-gray-100 px-2 py-1 rounded">ourpickup.com/producer/{user.slug}</code>
          <button onClick={() => navigator.clipboard.writeText(`https://ourpickup.com/producer/${user.slug}`)} className="ml-2 text-blue-600 text-xs">Copy</button>
        </div>
      )}
      
      <Accordion 
        items={accordionItems} 
        defaultOpen={0}
        className="mt-4"
        headerClassName="text-base font-medium py-4"
        iconOpen="▼"
        iconClosed="▶"
      />
    </div>
  );
}
